/**
 * InvoiceForm – buildFormState
 *
 * Builds the on-the-wire `InvoiceFormState` payload from the live `useInvoiceForm`
 * state. Extracted from InvoiceForm.tsx to keep the JSX file under the
 * max-lines-per-function / complexity limits.
 */

import type { Address } from '@/gen/proto'

import { toLocalDateString } from './invoice-form-helpers'
import { type useInvoiceForm, type AddressSnapshot } from './useInvoiceForm'
import { addressHasUsage } from '../../../utils/billing-helpers'
import { toSmallestUnit } from '../../../utils/formatters'

import type { InvoiceFormState, InvoiceItemFormState } from '../../../types/billing'

type Form = ReturnType<typeof useInvoiceForm>

// ─────────────────────────────────────────────────────────────────────────────
// Line item conversion
// ─────────────────────────────────────────────────────────────────────────────

function splitGstRate(
  gstRate: number,
  supplyType: string,
): { cgstRate: string; sgstRate: string; igstRate: string } {
  if (supplyType === 'intrastate') {
    const half = (gstRate / 2).toFixed(2)
    return { cgstRate: half, sgstRate: half, igstRate: '0.00' }
  }
  if (supplyType === 'interstate') {
    return { cgstRate: '0.00', sgstRate: '0.00', igstRate: gstRate.toFixed(2) }
  }
  return { cgstRate: '0.00', sgstRate: '0.00', igstRate: '0.00' }
}

function buildFormLineItems(form: Form): InvoiceItemFormState[] {
  return form.lineItems.map((item) => {
    const gstRate = parseFloat(item.gstRate) || 0
    const { cgstRate, sgstRate, igstRate } = splitGstRate(gstRate, form.supplyType)
    const formItem: InvoiceItemFormState = {
      id: item.id,
      description: item.description,
      quantity: (parseFloat(item.quantity) || 0).toString(),
      unit: item.unit,
      unitPrice: toSmallestUnit(parseFloat(item.unitPrice) || 0, form.currency),
      discountPercent: (parseFloat(item.discountPercent) || 0).toFixed(2),
      gstRate: gstRate.toFixed(2),
      cgstRate, sgstRate, igstRate,
    }
    if (item.itemId) { formItem.itemId = item.itemId }
    if (item.accountId) { formItem.accountId = item.accountId }
    if (item.itemCode) { formItem.itemCode = item.itemCode }
    if (item.hsnCode) { formItem.hsnCode = item.hsnCode }
    if (item.sacCode) { formItem.sacCode = item.sacCode }
    return formItem
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Base payload
// ─────────────────────────────────────────────────────────────────────────────

function buildBaseFormState(form: Form, items: InvoiceItemFormState[]): InvoiceFormState {
  return {
    invoiceType: form.invoiceType,
    invoiceNumber: form.invoiceNumber || undefined,
    supplierBillNumber: form.supplierBillNumber || undefined,
    invoiceDate: form.invoiceDate ? toLocalDateString(form.invoiceDate) : '',
    partyId: form.isWalkIn ? undefined : form.selectedPartyId,
    isWalkIn: form.isWalkIn || undefined,
    cashAccountId: form.isWalkIn && form.selectedCashAccountId ? form.selectedCashAccountId : undefined,
    currency: form.currency,
    exchangeRate: form.exchangeRate,
    items,
    notes: form.notes,
    termsConditions: form.termsConditions,
    paymentTerms: form.paymentTerms,
    roundOffAmount: form.totals.roundOffAmount,
    changeNotes: form.isEditingPosted ? (form.changeNotes || undefined) : undefined,
  }
}

function applyOptionalFields(formState: InvoiceFormState, form: Form): void {
  if (form.dueDate) { formState.dueDate = toLocalDateString(form.dueDate) }
  if (form.poNumber) { formState.poNumber = form.poNumber }
  if (form.partyVendorCode) { formState.partyVendorCode = form.partyVendorCode }
  if (form.placeOfSupply) { formState.placeOfSupply = form.placeOfSupply }
  if (form.partyRole && !form.isWalkIn) { formState.partyRole = form.partyRole }
}

function applyPartyAddress(formState: InvoiceFormState, form: Form): void {
  const partyBillingAddress =
    form.invoiceType === 'export' && form.exportType === 'services'
      ? form.selectedConsigneeAddress
      : form.selectedShipToAddress
  if (partyBillingAddress) {
    formState.partyAddressId = partyBillingAddress.id
    formState.partyAddress = partyBillingAddress
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Bank details
// ─────────────────────────────────────────────────────────────────────────────

function applyBankDetails(formState: InvoiceFormState, form: Form): void {
  const id = form.selectedBankAccountId
  if (!id) { return }
  if (id.startsWith('bank:')) {
    applyRegisteredBankAccount(formState, form, id.slice(5))
  } else if (id.startsWith('gl:')) {
    applyGLBankAccount(formState, form, id.slice(3))
  }
}

type RegisteredBank = Form['bankAccounts'][number]

function toBankDetails(bank: RegisteredBank): NonNullable<InvoiceFormState['bankDetails']> {
  return {
    bankName: bank.bankName || '',
    accountName: bank.nameAtBank || bank.accountName,
    accountNumber: bank.accountNumber || '',
    ifsc: bank.ifscCode || '',
    branch: bank.branch || '',
    swiftCode: bank.swiftCode || '',
    iban: bank.iban || '',
    routingNumber: bank.routingNumber || '',
    sortCode: bank.sortCode || '',
  }
}

function applyRegisteredBankAccount(formState: InvoiceFormState, form: Form, accountId: string): void {
  const selectedBank = form.bankAccounts.find((acc) => acc.id === accountId)
  if (!selectedBank) { return }
  formState.bankDetails = toBankDetails(selectedBank)
}

function applyGLBankAccount(formState: InvoiceFormState, form: Form, accountId: string): void {
  const glAccount = form.bankGLAccounts.find((acc) => acc.id === accountId)
  if (!glAccount) { return }
  formState.bankDetails = {
    bankName: '',
    accountName: glAccount.name,
    accountNumber: '',
    ifsc: '',
    branch: '',
    swiftCode: '',
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Address fields
// ─────────────────────────────────────────────────────────────────────────────

function applyB2BShipToAddress(formState: InvoiceFormState, form: Form): void {
  const isB2BLike =
    form.invoiceType === 'b2b' || form.invoiceType === 'b2c' || form.invoiceType === 'vendor_bill'
  if (!isB2BLike || !form.selectedConsigneeAddress) { return }
  formState.shipToAddressId = form.selectedConsigneeAddress.id
  formState.shipToAddress = form.selectedConsigneeAddress
}

function applyExportFields(formState: InvoiceFormState, form: Form): void {
  if (form.invoiceType !== 'export') { return }
  formState.exportType = form.exportType
  if (form.lutNumber) { formState.lutNumber = form.lutNumber }
  if (form.lutDate) { formState.lutDate = toLocalDateString(form.lutDate) }
  if (form.countryOfDestination) { formState.countryOfDestination = form.countryOfDestination }

  if (form.exportType === 'goods' && form.selectedShipToAddress) {
    formState.shipToAddressId = form.selectedShipToAddress.id
    formState.shipToAddress = form.selectedShipToAddress
  }
  if (form.exportType === 'goods') {
    buildConsigneeAddress(formState, form.consigneeType, form.allPartyAddresses, form.consigneeName, form.customConsigneeAddress)
    buildNotifyPartyAddress(formState, form.notifyPartyType, form.allPartyAddresses, form.notifyPartyName, form.customNotifyPartyAddress)
    applyShippingDetails(formState, form)
  }
}

function applyShippingDetails(formState: InvoiceFormState, form: Form): void {
  if (form.portOfLoading) { formState.portOfLoading = form.portOfLoading }
  if (form.portOfDischarge) { formState.portOfDischarge = form.portOfDischarge }
  if (form.shippingBillNumber) { formState.shippingBillNumber = form.shippingBillNumber }
  if (form.shippingBillDate) { formState.shippingBillDate = toLocalDateString(form.shippingBillDate) }
  if (form.portCode) { formState.portCode = form.portCode }
  if (form.modeOfTransport) { formState.modeOfTransport = form.modeOfTransport }
  if (form.vesselFlightNumber) { formState.vesselFlightNumber = form.vesselFlightNumber }
  if (form.blAwbNumber) { formState.blAwbNumber = form.blAwbNumber }
  if (form.containerNumbers) { formState.containerNumbers = form.containerNumbers }
}

function buildConsigneeAddress(
  formState: InvoiceFormState,
  consigneeType: string,
  allPartyAddresses: Address[],
  consigneeName: string,
  customConsigneeAddress: AddressSnapshot,
): void {
  if (consigneeType === 'same') {
    const billingAddr = allPartyAddresses.find((a) => addressHasUsage(a, 'billing'))
    if (billingAddr) {
      formState.consigneeAddressId = billingAddr.id
      formState.consigneeAddress = billingAddr
    }
    return
  }
  if (consigneeType === 'party') {
    const consigneeAddr = allPartyAddresses.find((a) => addressHasUsage(a, 'consignee'))
    if (consigneeAddr) {
      formState.consigneeAddressId = consigneeAddr.id
      formState.consigneeAddress = consigneeAddr
    }
    return
  }
  if (consigneeType === 'custom' && customConsigneeAddress.line1) {
    formState.consigneeAddress = {
      label: consigneeName || 'Custom Consignee',
      type: 'consignee',
      ...customConsigneeAddress,
    }
    formState.consigneeName = consigneeName
  }
}

function buildNotifyPartyAddress(
  formState: InvoiceFormState,
  notifyPartyType: string,
  allPartyAddresses: Address[],
  notifyPartyName: string,
  customNotifyPartyAddress: AddressSnapshot,
): void {
  if (notifyPartyType === 'same') {
    const billingAddr = allPartyAddresses.find((a) => addressHasUsage(a, 'billing'))
    if (billingAddr) {
      formState.notifyPartyAddressId = billingAddr.id
      formState.notifyPartyAddress = billingAddr
    }
    return
  }
  if (notifyPartyType === 'custom' && customNotifyPartyAddress.line1) {
    formState.notifyPartyAddress = {
      label: notifyPartyName || 'Custom Notify Party',
      type: 'notify_party',
      ...customNotifyPartyAddress,
    }
    formState.notifyPartyName = notifyPartyName
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public builder
// ─────────────────────────────────────────────────────────────────────────────

export function buildFormState(form: Form): InvoiceFormState {
  const items = buildFormLineItems(form)
  const formState = buildBaseFormState(form, items)
  applyOptionalFields(formState, form)
  applyPartyAddress(formState, form)
  applyBankDetails(formState, form)
  applyB2BShipToAddress(formState, form)
  applyExportFields(formState, form)
  return formState
}
